import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUp, Mic, Square, Sparkles, Dna, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { api, errText, newKey } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DnaPanel } from "@/components/DnaPanel";
import { FieldProposals } from "@/components/FieldProposals";
import { DirectionChoice, MaterialsCard, MaterialsFound, Thumb, WebsiteFound } from "@/components/Materials";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { isMinistry } from "@/lib/vocab";
import { cn } from "@/lib/utils";

function Bubble({ m, onAction, materials, onState, showCard }) {
  const me = m.role === "user";
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} className={cn("flex", me ? "justify-end" : "justify-start")} data-testid={`message-${m.role}`}>
      <div className={cn("max-w-[88%] sm:max-w-[78%]", me ? "rounded-3xl rounded-br-md bg-foreground text-background px-5 py-3.5" : "")}>
        {!me && <div className="flex items-center gap-2 mb-2"><span className="h-6 w-6 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center"><Sparkles className="h-3 w-3 text-gold" /></span><span className="eyebrow-label">Legacy Persona</span></div>}
        <p className={cn("text-[15px] leading-relaxed whitespace-pre-wrap", !me && "font-display text-lg sm:text-xl leading-snug")}>{m.content}</p>
        {me && m.attachments?.length > 0 && <div className="flex flex-wrap gap-2 mt-3" data-testid="message-attachments">{m.attachments.map((a) => <Thumb key={a.id} u={a} size="h-14 w-14" />)}</div>}
        {!me && m.materials && <MaterialsFound found={m.materials} />}
        {!me && m.website && <WebsiteFound found={m.website} messageId={m.id} onState={onState} />}
        {!me && <FieldProposals message={m} onAction={onAction} />}
        {!me && m.ui === "materials" && showCard && <MaterialsCard materials={materials} onState={onState} />}
      </div>
    </motion.div>
  );
}

export default function Persona() {
  const { org } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [ready, setReady] = useState(false);
  const [dnaOpen, setDnaOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [direction, setDirection] = useState("keep");
  const [revealOpen, setRevealOpen] = useState(false);
  const recRef = useRef(null);
  const endRef = useRef(null);
  const taRef = useRef(null);

  useEffect(() => {
    api.post("/persona/start").then((r) => setState(r.data)).catch((e) => toast.error(errText(e)));
  }, []);

  const analyzing = !!state?.materials?.analyzing || !!state?.materials?.website?.importing;
  useEffect(() => {
    if (!analyzing) return;
    const t = setInterval(() => api.get("/persona/messages").then((r) => setState(r.data)).catch(() => {}), 2500);
    return () => clearInterval(t);
  }, [analyzing]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [state?.messages?.length, busy]);

  const send = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    const optimistic = { id: `tmp-${Date.now()}`, role: "user", content: t, field_updates: [] };
    setState((s) => ({ ...s, messages: [...(s?.messages || []), optimistic] }));
    setText("");
    try {
      const { data } = await api.post("/persona/message", { text: t, message_id: newKey() });
      setState(data);
    } catch (err) {
      toast.error(errText(err));
      setState((s) => ({ ...s, messages: s.messages.filter((m) => m.id !== optimistic.id) }));
      setText(t);
    } finally {
      setBusy(false);
      taRef.current?.focus();
    }
  };

  const onAction = async (payload) => {
    try {
      const { data } = await api.post("/persona/field-action", payload);
      setState(data);
    } catch (err) {
      toast.error(errText(err));
    }
  };
  const saveField = async (field, value) => {
    try { const { data } = await api.patch("/dna/field", { field, value }); setState((s) => ({ ...s, ...data })); toast.success("Saved"); } catch (err) { toast.error(errText(err)); }
  };
  const lockField = async (field, locked) => {
    try { const { data } = await api.post("/dna/lock", { field, locked }); setState((s) => ({ ...s, ...data })); } catch (err) { toast.error(errText(err)); }
  };

  const toggleRecord = async () => {
    if (recording) { recRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      rec.ondataavailable = (ev) => ev.data.size && chunks.push(ev.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        setRecording(false);
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 1000) return;
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, `voice.${(rec.mimeType || "audio/webm").includes("mp4") ? "mp4" : "webm"}`);
          const { data } = await api.post("/persona/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setText((t) => (t ? `${t} ${data.text}` : data.text));
          taRef.current?.focus();
        } catch (err) {
          toast.error(errText(err));
        } finally {
          setTranscribing(false);
        }
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch {
      toast.error("Microphone access was blocked. You can type instead.");
    }
  };

  const ratio = state?.completeness?.ratio || 0;
  const canReveal = ratio >= 0.8 || ready;
  const cost = state?.message_cost || 0;
  const materials = state?.materials;
  const hasMaterials = !!materials?.has_materials;
  const showInlineCard = materials && materials.status !== "skipped" && !(materials.uploads?.length > 0 && materials.pending_count === 0) && !materials.website?.import;
  const goReveal = () => {
    if (hasMaterials && !revealOpen) { setRevealOpen(true); return; }
    navigate("/brand", { state: { autostart: true, direction: hasMaterials ? direction : undefined } });
  };

  return (
    <div className="grid lg:grid-cols-[1fr_400px] xl:grid-cols-[1fr_440px] min-h-[calc(100vh-4rem)]" data-testid="persona-page">
      <section className="flex flex-col min-w-0 min-h-[calc(100vh-4rem)] lg:h-[calc(100vh-4rem)] lg:sticky lg:top-16">
        <header className="flex items-center justify-between gap-3 px-4 sm:px-8 h-14 border-b border-border">
          <div className="flex items-center gap-3 min-w-0">
            <span className="eyebrow-label truncate">Legacy Persona · {org?.name}</span>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setDnaOpen(true)} data-testid="open-dna-sheet" className="lg:hidden btn-outline h-9 px-3 text-xs"><Dna className="h-3.5 w-3.5 text-gold" />{Math.round(ratio * 100)}%</button>
            {canReveal ? (
              <button type="button" onClick={goReveal} className="btn-gold h-9 px-4 text-xs sm:text-sm" data-testid="reveal-brand-button">Reveal my brand</button>
            ) : (
              <button type="button" onClick={() => setReady(true)} className="btn-quiet h-9 text-xs" data-testid="im-ready-button">I'm ready</button>
            )}
          </div>
        </header>
        {isMinistry(org)&&<div className="px-4 sm:px-8 py-3 border-b border-border text-xs text-muted-foreground">Your mission, people served and community impact guide this conversation. <Link className="underline" to="/grow">Review mission profile</Link></div>}
        {revealOpen && hasMaterials && (
          <div className="px-4 sm:px-8 py-4 border-b border-border surface" data-testid="reveal-direction-panel">
            <p className="text-sm mb-3">You shared brand materials. How should your Brand Reveal treat them?</p>
            <DirectionChoice value={direction} onChange={setDirection} />
            <div className="flex items-center gap-2 mt-3">
              <button type="button" onClick={goReveal} className="btn-gold h-9 px-4 text-xs sm:text-sm" data-testid="reveal-confirm-button">Continue to Brand Reveal</button>
              <button type="button" onClick={() => setRevealOpen(false)} className="btn-quiet h-9 text-xs" data-testid="reveal-cancel-button">Not yet</button>
            </div>
          </div>
        )}
        <div className="flex-1 overflow-y-auto scrollbar-thin px-4 sm:px-8 py-8 space-y-7" data-testid="message-list">
          {!state && <div className="text-sm text-muted-foreground font-display italic">Legacy Persona is preparing your first question…</div>}
          <AnimatePresence initial={false}>
            {state?.messages?.map((m) => <Bubble key={m.id} m={m} onAction={onAction} materials={materials} onState={setState} showCard={showInlineCard} />)}
          </AnimatePresence>
          {attachOpen && <div className="flex justify-start"><MaterialsCard materials={materials} onState={setState} onClose={() => setAttachOpen(false)} compact /></div>}
          {analyzing && <div className="flex items-center gap-2 text-sm text-muted-foreground font-display italic" data-testid="materials-analyzing"><span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />{materials?.website?.importing ? "Reading your website…" : "Reading your materials…"}</div>}
          {busy && <div className="flex items-center gap-2 text-sm text-muted-foreground font-display italic" data-testid="persona-thinking"><span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />Listening…</div>}
          <div ref={endRef} />
        </div>
        <form onSubmit={send} className="border-t border-border px-4 sm:px-8 py-4" data-testid="persona-input-form">
          <div className="flex items-end gap-2 rounded-3xl border border-border surface px-3 py-2 focus-within:border-gold/60 transition-colors">
            <button type="button" onClick={() => setAttachOpen((v) => !v)} aria-label="Add brand materials" data-testid="persona-attach-button" className={cn("h-10 w-10 rounded-full flex items-center justify-center transition-colors", attachOpen ? "bg-gold/15 text-gold" : "hover:bg-muted text-muted-foreground")}><Paperclip className="h-4 w-4" /></button>
            <textarea ref={taRef} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }} rows={1} placeholder={transcribing ? "Transcribing…" : "Talk or type — in your own words"} data-testid="persona-input" className="flex-1 bg-transparent resize-none text-[15px] leading-relaxed py-2 px-2 max-h-40 focus:outline-none" style={{ height: "auto" }} onInput={(e) => { e.target.style.height = "auto"; e.target.style.height = `${Math.min(e.target.scrollHeight, 160)}px`; }} />
            <button type="button" onClick={toggleRecord} aria-label={recording ? "Stop recording" : "Speak"} data-testid="persona-mic-button" className={cn("h-10 w-10 rounded-full flex items-center justify-center transition-colors", recording ? "bg-destructive text-white" : "hover:bg-muted text-muted-foreground")}>{recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}</button>
            <button type="submit" disabled={!text.trim() || busy} aria-label="Send" data-testid="persona-send-button" className="h-10 w-10 rounded-full bg-gold text-black flex items-center justify-center disabled:opacity-40 transition-opacity"><ArrowUp className="h-4 w-4" /></button>
          </div>
          <div className="flex items-center justify-between mt-2 px-2 text-[11px] text-muted-foreground">
            <span data-testid="persona-cost">{cost ? `${cost} credits per message` : "Interview messages are free until your first Brand Reveal"} · voice 1 credit</span>
            <span className="hidden sm:inline">Enter to send · Shift+Enter for a new line</span>
          </div>
        </form>
      </section>
      <aside className="hidden lg:block border-l border-border px-5 py-6 overflow-y-auto scrollbar-thin h-[calc(100vh-4rem)] sticky top-16 surface" data-testid="dna-aside">
        <DnaPanel dna={state?.dna} fields={state?.fields} completeness={state?.completeness} onSave={saveField} onLock={lockField} />
      </aside>
      <Sheet open={dnaOpen} onOpenChange={setDnaOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetTitle className="font-display text-xl mb-4">Your DNA</SheetTitle>
          <DnaPanel dna={state?.dna} fields={state?.fields} completeness={state?.completeness} onSave={saveField} onLock={lockField} />
        </SheetContent>
      </Sheet>
    </div>
  );
}
